import { Injectable, ComponentRef, ViewContainerRef, ComponentFactoryResolver, Injector } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { InvoiceTemplate1Component } from '../../shared/invoice-templates/template1/invoice-template1.component';
import { InvoiceTemplate2Component } from '../../shared/invoice-templates/template2/invoice-template2.component';
import { InvoiceTemplate3Component } from '../../shared/invoice-templates/template3/invoice-template3.component';

@Injectable({
	providedIn: 'root'
})
export class InvoiceTemplateService {

	constructor(
		private translateService: TranslateService,
		private componentFactoryResolver: ComponentFactoryResolver,
		private injector: Injector
	) {}

	/**
	 * Get the template number from invoice data
	 * @param invoiceData - The invoice data from API
	 * @returns Template number (1, 2, or 3)
	 */
	getTemplateNumber(invoiceData: any): number {
		const templateNumber = invoiceData?.company?.template?.number || 1;

		return templateNumber;
	}

	/**
	 * Get the appropriate template component class based on template number
	 * @param templateNumber - The template number
	 * @returns The component class
	 */
	getTemplateComponent(templateNumber: number): any {
		// determine whether the current viewport should use the mobile template
		const isBrowser = typeof window !== 'undefined' && !!window.innerWidth;
		const MOBILE_BREAKPOINT = 768; // px
		const isMobile = isBrowser ? window.innerWidth <= MOBILE_BREAKPOINT : false;

		// Template 1 is intended for mobile only. On desktop, fall back to a
		// desktop-friendly template (template 2) which is a normal PDF/print view.
		switch (templateNumber) {
			case 1:
				return isMobile ? InvoiceTemplate1Component : InvoiceTemplate2Component;
			case 2:
				return InvoiceTemplate2Component;
			case 3:
				return InvoiceTemplate3Component;
			default:
				console.warn(`Unknown template number: ${templateNumber}, using template 2 as desktop fallback`);
				return InvoiceTemplate2Component;
		}
	}

	/**
	 * Create and render the template component dynamically
	 * @param templateNumber - The template number
	 * @param invoiceData - The invoice data
	 * @param lang - The language
	 * @param viewContainerRef - The view container to render into
	 * @returns ComponentRef of the created component
	 */
	createTemplateComponent(
		templateNumber: number,
		invoiceData: any,
		lang: 'en' | 'ar' | 'he',
		viewContainerRef: ViewContainerRef
	): ComponentRef<any> {
		viewContainerRef.clear();

		const componentClass = this.getTemplateComponent(templateNumber);

		const componentFactory = this.componentFactoryResolver.resolveComponentFactory(componentClass);

		const componentRef = viewContainerRef.createComponent(componentFactory, 0, this.injector);

		const instance = componentRef.instance as any;
		instance.invoiceData = invoiceData;
		instance.lang = lang;
		
		// Load translations for the specific language before component initialization
		// This ensures the correct language is used from the start
		try {
			// Load translations WITHOUT changing global language
			this.translateService.getTranslation(lang).subscribe(trans => {
				instance.localTranslations = trans || {};
				// Trigger change detection after translations are loaded
				componentRef.changeDetectorRef.detectChanges();
			}, err => {
				console.warn('[InvoiceTemplateService] Failed to load translations for', lang, err);
			});
		} catch (e) {
			console.warn('[InvoiceTemplateService] Failed to load translations for', lang, e);
		}
		
		instance.showInvoiceStop = false;
		instance.invoiceId = invoiceData.invoice_id;
		instance.userOptions = false;
		instance.pdfLink = invoiceData.pdf_link;

		// Initial detect so component initializes. Afterwards, ensure translations for the
		// requested language are loaded and trigger another change detection so translate
		// pipes update their values (this avoids rendering literal keys if translations
		// aren't yet available at the first render).
		componentRef.changeDetectorRef.detectChanges();

		try {
			// preload translations for the requested language without changing the
			// application's active language. Using getTranslation avoids calling
			// TranslateService.use which switches the global language and causes
			// unintended side-effects elsewhere in the app (e.g. table headers).
			this.translateService.getTranslation(lang).subscribe(() => {
				// translations available for requested language - refresh the component view
				componentRef.changeDetectorRef.detectChanges();
			});
		} catch (e) {
			// If translation loading fails for some reason, we still return the component.
			console.warn('Failed to preload translations for invoice template', e);
		}

		return componentRef;
	}

	/**
	 * Get the HTML content from a rendered component
	 * @param componentRef - The component reference
	 * @returns HTML string
	 */
	getComponentHTML(componentRef: ComponentRef<any>): string {
		const element = componentRef.location.nativeElement;
		return element.outerHTML;
	}

	/**
	 * Generate invoice HTML by creating component and extracting HTML
	 * @param invoiceData - The invoice data
	 * @param lang - The language
	 * @param viewContainerRef - The view container
	 * @returns HTML string
	 */
	async generateInvoiceHTML(
		invoiceData: any,
		lang: 'en' | 'ar' | 'he' = 'he',
		viewContainerRef: ViewContainerRef
	): Promise<string> {
		const templateNumber = this.getTemplateNumber(invoiceData);
		const componentRef = this.createTemplateComponent(templateNumber, invoiceData, lang, viewContainerRef);
		const element = componentRef.location.nativeElement as HTMLElement;

		// Use already loaded translations instead of making new API calls
		let translationMap: any = {};
		try {
			// Get translations from the service's internal store instead of making API calls
			translationMap = this.translateService.translations[lang] || {};
			// Ensure change detection runs so pipes update (best-effort)
			componentRef.changeDetectorRef.detectChanges();
		} catch (e) {
			console.warn('generateInvoiceHTML: failed to get cached translations', e);
		}

		// Give the created component the loaded translations so its
		// translateKeyLocal helpers and template bindings can resolve
		// translations locally (this mirrors InvoiceTemplate1Component behavior).
		try {
			const instance: any = (componentRef && (componentRef.instance as any)) || null;
			if (instance) {
				// prefer the translation map we just loaded; if empty, leave as-is
				instance.localTranslations = translationMap || instance.localTranslations || {};
				// ensure change detection runs so bindings update synchronously
				componentRef.changeDetectorRef.detectChanges();
			}
		} catch (e) {
			// non-fatal - fall back to reading whatever rendered output exists
			console.warn('generateInvoiceHTML: failed to inject localTranslations into component', e);
		}

		let fragmentHtml = element.outerHTML;

		// Before destroying the component, copy any style tags from the document head
		// that relate to the component (Angular emulated styles) or look relevant
		// for invoice rendering (keywords). This ensures the returned HTML can
		// be rendered in an isolated iframe with its styles intact.
		let headHtml = '';
		try {
			const keys = ['.page', '.payment-table', '.totals', '.invoice', '.toolbar', '[_nghost', '[_ngcontent'];
			// copy <link rel="stylesheet"> entries so external CSS (fonts, global styles) load
			Array.from(document.head.querySelectorAll('link[rel="stylesheet"]')).forEach((link: Element) => {
				const href = (link as HTMLLinkElement).href;
				if (href) {
					headHtml += `<link rel="stylesheet" href="${href}">\n`;
				}
			});

			// Copy inline <style> rules that match keywords or nghost attributes
			Array.from(document.head.querySelectorAll('style')).forEach((styleEl: Element) => {
				try {
					const text = styleEl.textContent || '';
					for (let k = 0; k < keys.length; k++) {
						if (text.indexOf(keys[k]) !== -1) {
							headHtml += `<style>${text}</style>`;
							break;
						}
					}
				} catch (e) {
					// ignore
				}
			});
		} catch (e) {
			// best-effort only
			console.warn('generateInvoiceHTML: could not copy head styles', e);
		}

		// If this is template 2 we want to ensure the invoice uses the exact
		// desktop template CSS the user provided. Use a deterministic head
		// including the Google font link and the full style block so the
		// iframe matches the desktop preview exactly.
		const dir = (lang === 'ar' || lang === 'he') ? 'rtl' : 'ltr';
		let full = '';
		try {
			if (templateNumber === 2) {
				// Build a localized title for the desktop head using the preloaded
				// translation map so the document title is correct for the requested
				// invoice language (don't rely on the app's global language).
				const resolve = (obj: any, path: string) => {
					try {
						return path.split('.').reduce((o: any, k: string) => (o && o[k] !== undefined) ? o[k] : null, obj) || null;
					} catch (e) {
						return null;
					}
				};
				const taxInvoiceTitle = (translationMap && resolve(translationMap, 'allDocuments.documentTypes.taxInvoice'))
					|| (translationMap && resolve(translationMap, 'allDocuments.documentTypes.invoiceWithReceipt'))
					|| (translationMap && resolve(translationMap, 'documentTypes.taxInvoice'))
					|| 'فاتورة ضريبية';
				const receiptTitle = (translationMap && resolve(translationMap, 'allDocuments.documentTypes.receipt'))
					|| (translationMap && resolve(translationMap, 'allDocuments.documentTypes.invoiceWithReceipt'))
					|| (translationMap && resolve(translationMap, 'documentTypes.receipt'))
					|| 'وصل';
				const desktopHead = `
				<meta charset="utf-8">
				<meta name="viewport" content="width=device-width,initial-scale=1" />
				<title>${taxInvoiceTitle} / ${receiptTitle}</title>
				<link href="https://fonts.googleapis.com/css2?family=Tajawal:wght@300;400;700&display=swap" rel="stylesheet">
				<style>
				:root{--primary:#e12027;--primary-ink:#ffffff;--ink:#1a1a1a;--muted:#6b6b6b;--border:#e6e6e6;--row:#f6f6f6;--panel:#f2f2f2}
				*{box-sizing:border-box}
				html,body{margin:0;padding:0;background:#fff;color:var(--ink);font-family:"Tajawal",system-ui,-apple-system,Segoe UI,Roboto,"Helvetica Neue",Arial}
				body{overflow-x:hidden}
				.page{max-width:1024px;margin:18px auto;padding:0 12px; direction: rtl}
				.muted{color:var(--muted);font-size:13px}
				.logo-top{display:flex;justify-content:flex-start;margin-bottom:10px}
				.logo-wrap{width:160px;height:100px;border:2px solid var(--border);border-radius:6px;background:#fff;overflow:hidden}
				.logo-wrap img{max-width:100%;max-height:100%;object-fit:contain;display:block}
				.header{display:flex;flex-direction:column;gap:8px;margin-bottom:14px}
				.company{align-self:flex-end; text-align:left}
				.title{text-align:center}
				.customer{align-self:flex-start; text-align:right}
				.doc-title{font-weight:700;font-size:20px;margin:4px 0}
				.badge{display:inline-block;background:#fff;border:1px solid var(--border);border-radius:999px;padding:4px 10px;font-size:12px;margin-inline-start:6px}
				.warning{margin:8px auto 12px;padding:10px 14px;background:#fff3cd;border:1px solid #f5c6cb;border-radius:6px;color:#856404;font-size:14px;text-align:center}
				.warning a{color:#d63333;font-weight:700;text-decoration:underline}
				.customer strong{display:block;margin-bottom:2px}
				.customer-name{display:block;font-weight:600}
				.date-above{margin:8px 0;text-align:left;font-size:13px;color:black}
				table{width:100%;border-collapse:collapse;table-layout:fixed;direction:rtl}
				th,td{padding:12px 10px;vertical-align:top;word-wrap:break-word;overflow-wrap:break-word}
				thead th{background:var(--primary);color:var(--primary-ink);border:1px solid var(--primary);text-align:center;font-weight:700}
				tbody td{border:1px solid var(--border);background:#fff;text-align:right;direction:rtl}
				tbody tr:nth-child(odd) td{background:var(--row)}
				td.qty{text-align:center}
				td.total{padding-right:12px;font-weight:700;font-size:14px}
				tr.with-desc td{border-bottom:none}
				tr.desc-row td{border:1px solid var(--border);border-top:none;padding-top:6px;background:#fff}
				.totals{margin-top:16px;display:flex;justify-content:flex-end}
				.totals .box{width:min(420px,100%);background:var(--panel);border:1px solid var(--border);border-radius:4px;display:grid;grid-template-columns:1fr auto;gap:10px;padding:12px 14px}
				.totals .label{color:#333;text-align:right}
				.totals .value{text-align:right;font-weight:700}
				.payments{margin-top:18px}
				.payments h3{margin-bottom:8px;font-size:16px;font-weight:700}
				.payment-table{width:100%;border-collapse:collapse}
				.payment-table th,.payment-table td{border:1px solid var(--border);padding:8px 10px;text-align:right}
				.payment-table thead th{background:var(--primary);color:var(--primary-ink)}
				.payment-table tfoot td{background:var(--row);font-weight:700}
				.payment-table tfoot td.label-total{text-align:end}
				@media (max-width:600px){.payment-table thead{display:none}.payment-table tbody tr{display:grid;grid-template-columns:1fr auto auto;grid-template-rows:auto auto;gap:4px 8px;padding:8px;border:1px solid var(--border);border-radius:6px;margin-bottom:10px;background:#fff}.payment-table td{border:none;padding:0}.payment-table td.method{grid-row:1;grid-column:1/3;font-weight:700}.payment-table td.amount{grid-row:1;grid-column:3;text-align:left;font-weight:700}.payment-table td.due{grid-row:2;grid-column:1}.payment-table td.bank{grid-row:2;grid-column:2}.payment-table td.doc{grid-row:2;grid-column:3}.payment-table tfoot{display:flex;justify-content:flex-end;gap:10px;padding:6px;border:1px solid var(--border);border-radius:6px;background:#f6f6f6}.payment-table tfoot td{border:none;background:none;font-weight:700;text-align:right;padding:10px}.payment-table tfoot td.label-total,.payment-table tfoot td.amount{flex:0 0 auto}}
				.toolbar{display:flex;gap:8px;justify-content:center;margin:22px 0 12px;flex-wrap:wrap}
				.btn{border:1px solid var(--border);background:#fff;padding:10px 14px;border-radius:10px;cursor:pointer;font-weight:700}
				.btn.red{border-color:var(--primary);color:var(--primary)}
				@media print{.toolbar{display:none}}@media (max-width:700px){table td,table th{font-size:13px;padding:6px 4px 6px 2px}}
				</style>`;
				full = `<!doctype html><html lang="${lang}" dir="${dir}"><head>${desktopHead}</head><body>${fragmentHtml}</body></html>`;
			} else {
				full = `<!doctype html><html lang="${lang}" dir="${dir}"><head><meta charset="utf-8">${headHtml}</head><body>${fragmentHtml}</body></html>`;
			}
		} catch (e) {
			full = `<!doctype html><html lang="${lang}" dir="${dir}"><head><meta charset="utf-8">${headHtml}</head><body>${fragmentHtml}</body></html>`;
		}

		componentRef.destroy();

		return full;
	}

	/**
	 * Render template component directly into view container
	 * This method keeps the component alive for interactive use
	 * @param invoiceData - The invoice data
	 * @param lang - The language
	 * @param viewContainerRef - The view container
	 * @returns ComponentRef for further manipulation
	 */
	renderTemplateComponent(
		invoiceData: any,
		lang: 'en' | 'ar' | 'he' = 'he',
		viewContainerRef: ViewContainerRef
	): ComponentRef<any> {
		const templateNumber = this.getTemplateNumber(invoiceData);

		return this.createTemplateComponent(templateNumber, invoiceData, lang, viewContainerRef);
	}
}
